import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Info } from 'lucide-react';
import StepTabs from './StepTabs';
import SaiProductFrame, { nextProductAfter } from './SaiProductFrame';
import { PRODUCT_ICONS } from './productIcons';
import { productByKey, stageByKey } from './products';
import { IDS } from './ids';
import { screenIdFor } from './trapezium';

// sai's ProductLayout, inside our shell: the kicker, the headline, the numbered
// steps, then the picture beside the step's words and points. The picture stays
// mounted across steps; the words re-enter on each step, but the Next button
// stays the same element.
//   screenKey — his screen key (e.g. viz-app); our screen ID comes from SCREEN_IDS
//   steps     — [{ key, nav, head, lede, points: [[title, sub], ...] }]; key is the
//               step's stable ID (<screen>.show-step-<key>)
//   active / onStep — the step showing, and how to change it
//   state     — the page's data-trapezium-state
//   visual    — the picture (a node)
//   controls  — optional controls under the step's words (part of the step)
//   above     — optional node between the headline and the steps
//   note      — optional small print under the points
//   wide      — the picture takes the full width, the words go below it
export default function ProductLayout({
  screenKey,
  steps,
  active,
  onStep,
  state,
  visual,
  controls,
  above,
  note,
  wide = false,
}) {
  const navigate = useNavigate();
  const screenId = screenIdFor(screenKey);
  const product = productByKey(screenKey);
  const stage = stageByKey(product.stage);
  const Icon = PRODUCT_ICONS[product.icon];
  const step = steps[active];
  const last = active === steps.length - 1;
  const nextProduct = nextProductAfter(screenId);
  const idBase = `product-${screenKey}`;
  const tabbed = steps.length > 1; // one step: no tab bar, just the stage

  const onMore = () => {
    if (!last) onStep(active + 1);
    else if (nextProduct) navigate(nextProduct.route);
  };

  return (
    <SaiProductFrame screenId={screenId} state={state}>
      <div className="prod-page" data-stage={product.stage}>
        <p className="prod-kicker">
          <i aria-hidden="true">
            <Icon size={15} />
          </i>
          {product.name}
          <span>· {stage ? stage.label : 'Foundation'}</span>
        </p>
        <h1>{product.line}</h1>
        {above}

        {tabbed ? (
          <StepTabs
            steps={steps.map((s) => s.nav)}
            active={active}
            onChange={onStep}
            idBase={idBase}
            trapeziumId={(i) => IDS.step(screenKey, steps[i].key)}
            label={`${product.name}: steps`}
          />
        ) : null}

        <div
          className="prod-stage"
          data-wide={wide ? 'true' : undefined}
          id={`${idBase}-panel`}
          role={tabbed ? 'tabpanel' : undefined}
          aria-labelledby={tabbed ? `${idBase}-tab-${active}` : undefined}
        >
          <div className="prod-visual prod-visual-live">{visual}</div>
          <div className="prod-copy">
            <div className="prod-copy-main" key={active}>
              <h2>{step.head}</h2>
              <p>{step.lede}</p>
              {controls}
            </div>
            <div className="prod-copy-side">
              {step.points.length ? (
                <ul className="prod-points" key={active}>
                  {step.points.map(([t, s], i) => (
                    <li key={t} style={{ '--i': i }}>
                      <i aria-hidden="true">
                        <Check size={14} strokeWidth={3} />
                      </i>
                      <span>
                        <b>{t}</b>
                        <span>{s}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
              {note ? (
                <p className="prod-note">
                  <Info size={14} aria-hidden="true" />
                  {note}
                </p>
              ) : null}
              {last && !nextProduct ? null : (
                <button type="button" className="prod-next" data-trapezium-id={IDS.more(screenKey)} onClick={onMore}>
                  {last ? `Next product: ${nextProduct.title}` : `Next: ${steps[active + 1].nav}`}
                  <ArrowRight size={16} aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </SaiProductFrame>
  );
}
